import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Form, Row, Select as AntSelect, Space, Typography } from 'antd'
import { includes, isEmpty, isNil, map, size } from 'lodash'
import PropTypes from 'prop-types'

import { SIZES } from '../../constants'
import { antFormItemNamePropType } from '../../prop-types'
import {
  filterSelectOptions,
  formatFormItemLabel,
  formatFormListLabel,
} from '../../utils'

import ListItemSelect from './list-item-select'

const FormListSelect = ({
  options,
  name,
  label,
  showCountItems,
  newItemSelectPlaceholder,
  form,
  rules,
  itemRules,
  isDirty,
  showColon,
  labelAbove,
  itemRender,
  ...rest
}) => {
  const [listSize, setListSize] = useState(size(form.getFieldValue(name)))

  const labelContent = useMemo(() => {
    const formListLabel = formatFormListLabel(label, listSize, showCountItems)
    return formatFormItemLabel(formListLabel, showColon)
  }, [listSize, label, showColon, showCountItems])

  const shouldModify = useCallback(
    value => {
      const existingValues = form.getFieldValue(name)
      return !includes(existingValues, value)
    },
    [form, name],
  )

  const handleAddItemWithValue = useCallback(
    add => value => {
      if (shouldModify(value)) {
        add(value)
        setListSize(previousSize => previousSize + 1)
      }
    },
    [shouldModify],
  )
  const handleRemoveItemByName = useCallback(
    remove => name => {
      remove(name)
      setListSize(previousSize => previousSize - 1)
    },
    [],
  )

  const listItemRender = useCallback(
    ({ key, name, fieldKey, ...restField }, remove) => {
      if (!isNil(itemRender)) {
        return itemRender({
          field: { key, name, fieldKey, ...restField },
          shouldModify,
          options,
          onClear: handleRemoveItemByName(remove),
          itemRules,
          ...rest,
        })
      }

      return (
        <Form.Item
          key={key}
          name={name}
          fieldKey={fieldKey}
          rules={itemRules}
          noStyle={isEmpty(itemRules)}
          {...restField}
        >
          <ListItemSelect
            shouldModify={shouldModify}
            name={name}
            options={options}
            onClear={handleRemoveItemByName(remove)}
            {...rest}
          />
        </Form.Item>
      )
    },
    [
      handleRemoveItemByName,
      itemRender,
      itemRules,
      rest,
      shouldModify,
      options,
    ],
  )

  useEffect(() => {
    setListSize(size(form.getFieldValue(name)))
  }, [form, isDirty, name])

  return (
    <Row align="middle">
      {labelAbove ? (
        <Col className="ant-form-item-label" span={24}>
          <Typography.Paragraph style={{ height: '32px', margin: 0 }}>
            {labelContent}
          </Typography.Paragraph>
        </Col>
      ) : (
        <Col span={3}>
          <Typography.Text>{labelContent}</Typography.Text>
        </Col>
      )}
      <Col span={labelAbove ? 24 : 21}>
        <Form.List rules={rules} label={label} name={name}>
          {(fields, { add, remove }, { errors }) => (
            <>
              <Space wrap>
                {map(fields, field => listItemRender(field, remove))}
                <Form.Item noStyle={isEmpty(itemRules)}>
                  <AntSelect
                    onChange={handleAddItemWithValue(add)}
                    placeholder={newItemSelectPlaceholder}
                    filterOption={filterSelectOptions}
                    style={{ minWidth: 150 }}
                    dropdownStyle={{ minWidth: 'fit-content' }}
                    options={options}
                    value={null}
                    dropdownMatchSelectWidth={SIZES.DROPDOWN_MIN_WIDTH}
                    {...rest}
                  />
                </Form.Item>
              </Space>
              <Row>
                <Form.ErrorList errors={errors} />
              </Row>
            </>
          )}
        </Form.List>
      </Col>
    </Row>
  )
}

FormListSelect.propTypes = {
  name: antFormItemNamePropType.isRequired,
  label: PropTypes.string,
  showCountItems: PropTypes.bool,
  newItemSelectPlaceholder: PropTypes.string,
  options: PropTypes.array.isRequired,
  rules: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  ),
  itemRules: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  ),
  form: PropTypes.object.isRequired,
  isDirty: PropTypes.bool,
  showColon: PropTypes.bool,
  labelAbove: PropTypes.bool,
  itemRender: PropTypes.func,
}

FormListSelect.defaultProps = {
  label: '',
  showCountItems: false,
  newItemSelectPlaceholder: 'Select a new item...',
  rules: undefined,
  itemRules: undefined,
  isDirty: false,
  showColon: false,
  labelAbove: true,
  itemRender: undefined,
}

export default FormListSelect
